import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { FileText, Mail, MapPin, Building, Globe } from 'lucide-react';

export const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-100 p-3 rounded-full">
              <FileText className="w-8 h-8 text-[#2D8B2E]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0A2F0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Terms and Conditions
            </h1>
          </div>
          <p className="text-gray-500">Last Updated: February 2025</p>
        </div>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">1. Acceptance of Terms</h2>
          <p className="text-gray-700 mb-4">
            Thank you for using <strong>Fudco</strong>. These Terms of Service ("Terms") govern your access to and use of the Fudco website
            (<a href="https://www.fudco.in" target="_blank" rel="noopener noreferrer" className="text-[#2D8B2E] hover:underline">www.fudco.in</a>), mobile applications, and related services (collectively, the "Fudco Platform").
          </p>
          <p className="text-gray-700 mb-4">
            By accessing or using the Fudco Platform, you agree to be legally bound by these Terms and enter into a binding agreement with
            <strong> Traderow Food Serve Private Limited</strong> ("Fudco", "we", "us", or "our").
          </p>
          <p className="text-gray-700">
            If you do not agree to these Terms, you must not use the Services.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">2. Restaurant Partners</h2>
          <p className="text-gray-700">
            "Restaurant" or "Restaurant Partner" refers to restaurants or merchants listed on the Fudco Platform.
          </p>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">3. Eligibility</h2>
          <p className="text-gray-700">
            You must be at least 18 years of age and legally capable of entering into a binding contract to use the Services.
            You agree to comply with all applicable laws and regulations.
          </p>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">4. Changes to Terms</h2>
          <p className="text-gray-700">
            Fudco reserves the right to modify these Terms at any time. Continued use of the Platform after changes
            constitutes acceptance of the revised Terms.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">5. Translation</h2>
          <p className="text-gray-700">
            Any translated version of these Terms is provided for convenience only. The English version shall prevail in case of conflict.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">6. Services</h2>
          <p className="text-gray-700">
            Fudco may modify, suspend, or discontinue any part of the Services at any time without notice.
            Updates or upgrades may be required to continue using the Services.
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">7. Disclaimers</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>All content is provided for informational purposes only.</li>
            <li>Fudco does not guarantee food quality, pricing accuracy, or availability.</li>
            <li>Certifications displayed are provided by Restaurant Partners and shown on an "as available" basis.</li>
            <li>Fudco may charge platform fees, delivery fees, or premium service fees in the future.</li>
          </ul>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">8. User Accounts</h2>
          <p className="text-gray-700">
            You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
            Providing false information or unauthorized business claims may lead to suspension or termination.
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">9. Food Ordering & Delivery</h2>
          <p className="text-gray-700 mb-4">
            Fudco acts solely as a technology intermediary connecting Customers, Restaurant Partners, and Delivery Partners.
            Fudco does not prepare, package, or deliver food directly.
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2">
            <li>Delivery may be performed by Restaurant Partners or third-party Delivery Partners.</li>
            <li>Delivery charges, surge fees, and taxes may apply.</li>
            <li>Fudco is not liable for delays, incorrect orders, or quality issues.</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">10. Payments, Cancellations & Refunds</h2>
          <p className="text-gray-700">
            Payments must be made through approved payment methods on the Platform.
            Refunds and cancellations are subject to Restaurant Partner approval and applicable policies.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">11. Intellectual Property</h2>
          <p className="text-gray-700">
            All content, trademarks, logos, and platform materials are the exclusive property of Fudco or its licensors.
            Unauthorized use is strictly prohibited.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">12. User Content</h2>
          <p className="text-gray-700">
            By submitting content, you grant Fudco a worldwide, royalty-free, perpetual license to use, modify, and distribute such content.
            You are solely responsible for the legality and accuracy of your content.
          </p>
        </section>

        {/* Section 13 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">13. Limitation of Liability</h2>
          <p className="text-gray-700">
            To the maximum extent permitted by law, Fudco shall not be liable for indirect, incidental, or consequential damages arising from use of the Services.
          </p>
        </section>

        {/* Section 14 */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">14. Governing Law</h2>
          <p className="text-gray-700">
            These Terms shall be governed by and construed in accordance with the laws of India.
          </p>
        </section>

        {/* Contact Box */}
        <section className="bg-gray-50 p-6 rounded-xl border-l-4 border-[#2D8B2E] mt-10">
          <h2 className="text-xl font-bold text-[#1b5e20] mb-4">Contact Us</h2>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Building className="w-5 h-5 text-[#2D8B2E] mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Legal Entity Name</p>
                <p className="text-gray-600">Traderow Food Serve Private Limited</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-[#2D8B2E] mt-1" />
              <div>
                <p className="font-semibold text-gray-800">CIN</p>
                <p className="text-gray-600">U56290OD2025PTC049467</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#2D8B2E] mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Registered Address</p>
                <p className="text-gray-600">At: Baragadia, PO: Baragadia<br />Jajpur, Odisha – 755026</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-[#2D8B2E] mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Website</p>
                <a href="https://www.fudco.in" target="_blank" rel="noopener noreferrer" className="text-[#2D8B2E] hover:underline">www.fudco.in</a>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#2D8B2E] mt-1" />
              <div>
                <p className="font-semibold text-gray-800">Email</p>
                <a href="mailto:service@fudco.in" className="text-[#2D8B2E] hover:underline">service@fudco.in</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
